import { KeyboardEvent, memo } from 'react'
import classNames from 'classnames'
import moment from 'jalali-moment'
import { IconType } from 'react-icons'
import { FiChevronLeft, FiMoreVertical } from 'react-icons/fi'
import {
    LuBell,
    LuCreditCard,
    LuLink,
    LuPhone,
    LuPiggyBank,
    LuUserPlus,
    LuWallet,
} from 'react-icons/lu'
import { fundName } from 'utilities/helper/format'
import { MessageKind, MessageTarget } from '../messageTarget'

interface KindMeta {
    /** Small caption next to the time, under a fund's name. */
    label: string
    /** The card's title when the message is not about one of our funds. */
    title: string
    Icon: IconType
    tile: string
}

export const KIND_META: Record<MessageKind, KindMeta> = {
    pay: {
        label: 'یادآوری پرداخت',
        title: 'یادآوری پرداخت',
        Icon: LuCreditCard,
        tile: 'bg-warning-soft text-warning-on',
    },
    fund: {
        label: 'صندوق',
        title: 'اطلاعیهٔ صندوق',
        Icon: LuPiggyBank,
        tile: 'bg-brand-soft text-brand-deep',
    },
    invite: {
        label: 'دعوت‌نامه',
        title: 'دعوت به صندوق',
        Icon: LuUserPlus,
        tile: 'bg-blue-6 text-blue-1',
    },
    wallet: {
        label: 'کیف پول',
        title: 'کیف پول',
        Icon: LuWallet,
        tile: 'bg-success-2 text-success-on',
    },
    link: {
        label: 'اطلاعیه',
        title: 'اطلاعیهٔ همیان',
        Icon: LuLink,
        tile: 'bg-blue-6 text-blue-4',
    },
    call: {
        label: 'تماس',
        title: 'تماس با همیان',
        Icon: LuPhone,
        tile: 'bg-brand-soft text-brand-strong',
    },
    info: {
        label: 'همیان',
        title: 'پیام همیان',
        Icon: LuBell,
        tile: 'bg-divider text-gray-1',
    },
}

export const messageTime = (timestamp: number) =>
    moment(new Date(timestamp * 1000))
        .locale('fa')
        .format('HH:mm')

interface MessageCardProps {
    message: IRequestMessageResultMessage
    target: MessageTarget
    /** Unread when this visit began — kept after the server marks it seen. */
    unread: boolean
    onOpen: (
        message: IRequestMessageResultMessage,
        target: MessageTarget
    ) => void
    onMenu: (message: IRequestMessageResultMessage) => void
}

/**
 * One inbox message.
 *
 * The whole card is the tap target when the message leads somewhere, and the
 * action row spells out where. A message with nowhere to go has no action
 * row, so a card that looks tappable always is. Deleting lives behind the ⋮
 * button rather than Android's long-press, which nobody discovers on the web.
 */
function MessageCard({
    message,
    target,
    unread,
    onOpen,
    onMenu,
}: MessageCardProps) {
    const meta = KIND_META[target.kind]
    const { Icon } = meta
    const clickable = Boolean(target.destination)
    const title = target.boxName ? fundName(target.boxName) : meta.title
    const time = messageTime(message.timestamp)

    const open = () => {
        if (clickable) onOpen(message, target)
    }
    const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
        if (!clickable || event.target !== event.currentTarget) return
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            open()
        }
    }

    return (
        <article
            role={clickable ? 'link' : undefined}
            tabIndex={clickable ? 0 : undefined}
            onClick={open}
            onKeyDown={onKeyDown}
            aria-label={clickable ? `${title}، ${target.cta}` : undefined}
            className={classNames(
                'relative rounded-card border p-4 transition-colors duration-200 animate-fade-in',
                unread
                    ? 'border-green-1/40 bg-brand-soft/60'
                    : 'border-divider bg-white',
                clickable &&
                    'cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-green-1/50 active:bg-sunken'
            )}
        >
            <div className="flex items-start gap-3">
                <span
                    className={classNames(
                        'flex h-10 w-10 shrink-0 items-center justify-center rounded-xl',
                        meta.tile
                    )}
                    aria-hidden
                >
                    <Icon className="h-5 w-5" />
                </span>

                <div className="flex-1 overflow-hidden pt-0.5">
                    <div className="flex items-center gap-1.5">
                        {unread && (
                            <span
                                className="h-2 w-2 shrink-0 rounded-full bg-green-1"
                                aria-label="خوانده‌نشده"
                            />
                        )}
                        <h3 className="truncate text-sm font-bold text-heading">
                            {title}
                        </h3>
                    </div>
                    <p className="mt-1 text-xs text-text-secondary">
                        {target.boxName ? `${meta.label} · ${time}` : time}
                    </p>
                </div>

                <button
                    type="button"
                    aria-label="گزینه‌های پیام"
                    onClick={(event) => {
                        event.stopPropagation()
                        onMenu(message)
                    }}
                    className="-me-2 -mt-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-text-muted transition-colors active:bg-divider"
                >
                    <FiMoreVertical className="h-[18px] w-[18px]" />
                </button>
            </div>

            <p className="mt-3 whitespace-pre-line break-words text-sm leading-7 text-text-body">
                {message.body}
            </p>

            {clickable && (
                <div
                    className={classNames(
                        'mt-3 flex items-center justify-between border-t pt-3',
                        unread ? 'border-green-1/20' : 'border-divider'
                    )}
                >
                    <span className="text-sm font-bold text-green-1">
                        {target.cta}
                    </span>
                    <FiChevronLeft
                        className="h-[18px] w-[18px] text-green-1"
                        aria-hidden
                    />
                </div>
            )}
        </article>
    )
}

export default memo(MessageCard)

/** Same silhouette as a card, so the list does not jump when data lands. */
export function MessageCardSkeleton() {
    return (
        <div className="animate-pulse rounded-card border border-divider bg-white p-4">
            <div className="flex items-start gap-3">
                <div className="h-10 w-10 shrink-0 rounded-xl bg-divider" />
                <div className="flex-1 pt-1">
                    <div className="h-3 w-32 rounded bg-divider" />
                    <div className="mt-2 h-2.5 w-16 rounded bg-divider" />
                </div>
            </div>
            <div className="mt-4 h-3 w-full rounded bg-divider" />
            <div className="mt-2 h-3 w-4/5 rounded bg-divider" />
        </div>
    )
}
